import { useState, useEffect, useRef } from 'react';
interface ServiceWorkerRegistration {
	waiting: ServiceWorker | null;
	installing: ServiceWorker | null;
	update: () => Promise<void>;
	addEventListener: (event: string, callback: (event: Event) => void) => void;
}

const VersionChecker: React.FC = () => {
	const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	useEffect(() => {
		if ('serviceWorker' in navigator) {
			registerServiceWorker();
		}
	}, []);

	useEffect(() => {
		if (registration) {
			const intervalId = setInterval(
				() => {
					registration.update();
				},
				30 * 60 * 1000,
			); // Check for updates every 30 minutes

			return () => clearInterval(intervalId);
		}
	}, [registration]);

	const registerServiceWorker = async (): Promise<void> => {
		try {
			const existingRegistration = await navigator.serviceWorker.getRegistration();

			if (existingRegistration) {
				setRegistration(existingRegistration as unknown as ServiceWorkerRegistration);

				existingRegistration.update();

				existingRegistration.addEventListener('updatefound', () => {
					const newWorker = existingRegistration.installing;

					if (newWorker) {
						newWorker.addEventListener('statechange', () => {
							if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
								handleRefresh();
							}
						});
					}
				});
			} else {
				const newRegistration = (await navigator.serviceWorker.register(
					'/service-worker.js',
				)) as unknown as ServiceWorkerRegistration;
				setRegistration(newRegistration);

				newRegistration.addEventListener('updatefound', () => {
					const newWorker = newRegistration.installing;

					if (newWorker) {
						newWorker.addEventListener('statechange', () => {
							if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
								handleRefresh();
							}
						});
					}
				});
			}
		} catch (error) {
			console.error('Service Worker registration failed:', error);
		}
	};

	const handleRefresh = (): void => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		if (registration?.waiting) {
			registration.waiting.postMessage('checkForUpdates');

			navigator.serviceWorker.addEventListener('controllerchange', () => {
				window.location.reload();
			});
		} else {
			window.location.reload();
		}
	};

	return null;
};

export default VersionChecker;
